import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useAppContext } from "../../../../AppContext";

/** Has to match the transition duration declared on .panel in Planner.module.scss. */
const TRANSITION_MS = 440;

/** Barely-there shrink the panel grows out of, so the slide has some depth to it. */
const LIFT_OFF_SCALE = 0.985;

type PanelPhase = "collapsed" | "expanding" | "expanded" | "collapsing";

export interface IPlannerPanelExpandResult {
  /** True only where the planner is stacked (calendar on top, classes below). */
  canExpand: boolean;
  isExpanded: boolean;
  /** True from the first frame of either direction until the slide settles. */
  isAnimating: boolean;
  toggleExpanded: () => void;
  /** Goes on the element wrapping the schedule panel. */
  panelRef: React.RefObject<HTMLDivElement>;
  /** Goes on the planner root, the area the expanded panel is allowed to cover. */
  areaRef: React.RefObject<HTMLDivElement>;
  /** Inline geometry for the panel while it is pinned over the calendar. */
  panelStyle: React.CSSProperties | undefined;
}

/**
 * Drives the schedule panel's "take the whole planner" slide on stacked layouts.
 *
 * The panel is laid out normally while collapsed. On expand it is pinned over the
 * planner area with `position: fixed` and the geometry measured from that area, then
 * FLIPped into place: it is put back where it just was, the browser is forced to
 * commit that, and only then is it released to its final spot. Only `transform`
 * animates, so the whole slide runs on the compositor and the list underneath is
 * never re-laid out mid-frame.
 */
export const usePlannerPanelExpand = (): IPlannerPanelExpandResult => {
  const { isDesktop } = useAppContext();
  const canExpand = !isDesktop;

  const [phase, setPhase] = useState<PanelPhase>("collapsed");
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties | undefined>(
    undefined,
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  /** Distance between the panel's place in the flow and the top of the planner area. */
  const flowOffsetRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const isExpanded = phase !== "collapsed";
  const isAnimating = phase === "expanding" || phase === "collapsing";

  const clearPendingWork = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const measureArea = useCallback(() => {
    const area = areaRef.current;

    if (!area) {
      return null;
    }

    const rect = area.getBoundingClientRect();

    if (rect.height <= 0 || rect.width <= 0) {
      return null;
    }

    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  /**
   * How far the panel's place in the normal flow sits below the top of the planner
   * area. While the panel is pinned it reports the area's own top, which would make
   * the slide back down a no-op, so it is briefly dropped out of its pinned position
   * for one synchronous measurement and put straight back.
   */
  const measureFlowOffset = useCallback(() => {
    const area = areaRef.current;
    const panel = panelRef.current;

    if (!area || !panel) {
      return null;
    }

    const areaTop = area.getBoundingClientRect().top;
    const isPinned = window.getComputedStyle(panel).position === "fixed";

    if (!isPinned) {
      return panel.getBoundingClientRect().top - areaTop;
    }

    const pinnedInlineStyle = panel.style.cssText;
    panel.style.position = "static";
    panel.style.transform = "none";
    const flowTop = panel.getBoundingClientRect().top;
    panel.style.cssText = pinnedInlineStyle;

    return flowTop - areaTop;
  }, []);

  const reset = useCallback(() => {
    clearPendingWork();
    flowOffsetRef.current = null;
    setPhase("collapsed");
    setPanelStyle(undefined);
  }, [clearPendingWork]);

  const expand = useCallback(() => {
    const area = measureArea();
    const offset = measureFlowOffset();

    clearPendingWork();

    // Without a measurement the panel still expands, it just snaps instead of
    // sliding. Better a working control than a dead one.
    if (!area || offset === null) {
      flowOffsetRef.current = null;
      setPanelStyle(undefined);
      setPhase("expanded");
      return;
    }

    flowOffsetRef.current = offset;
    setPanelStyle(area);
    setPhase("expanding");

    timeoutRef.current = window.setTimeout(
      () => setPhase("expanded"),
      TRANSITION_MS,
    );
  }, [clearPendingWork, measureArea, measureFlowOffset]);

  const collapse = useCallback(() => {
    const offset = measureFlowOffset();

    clearPendingWork();

    if (offset === null) {
      reset();
      return;
    }

    flowOffsetRef.current = offset;
    setPhase("collapsing");

    timeoutRef.current = window.setTimeout(() => {
      flowOffsetRef.current = null;
      setPhase("collapsed");
      setPanelStyle(undefined);
    }, TRANSITION_MS);
  }, [clearPendingWork, measureFlowOffset, reset]);

  const toggleExpanded = useCallback(() => {
    // Mid-slide clicks are dropped rather than reversed: restarting from a partly
    // applied transform would need the live matrix and buys very little.
    if (!canExpand || isAnimating) {
      return;
    }

    if (phase === "expanded") {
      collapse();
    } else {
      expand();
    }
  }, [canExpand, collapse, expand, isAnimating, phase]);

  /**
   * The transform is driven straight on the node rather than through React state.
   * A FLIP needs the browser to commit the "from" transform before the "to" one is
   * set; two React renders give no such guarantee, and folding both into a single
   * style recalculation is exactly what makes a panel teleport instead of slide.
   */
  useLayoutEffect(() => {
    const panel = panelRef.current;

    if (!panel) {
      return;
    }

    const offset = flowOffsetRef.current;

    if (phase === "expanding" && offset !== null) {
      panel.style.transition = "none";
      panel.style.transform = `translateY(${offset}px) scale(${LIFT_OFF_SCALE})`;
      // Reading a layout property forces the style above to be committed. This line
      // is the whole trick; drop it and the two writes collapse into one recalc.
      void panel.offsetHeight;
      // Hand the transition back to the stylesheet, which is where reduced-motion
      // users get it switched off.
      panel.style.transition = "";
      panel.style.transform = "translateY(0) scale(1)";
      return;
    }

    if (phase === "collapsing" && offset !== null) {
      panel.style.transition = "";
      panel.style.transform = `translateY(${offset}px) scale(${LIFT_OFF_SCALE})`;
      return;
    }

    panel.style.transition = "";
    panel.style.transform =
      phase === "expanded" ? "translateY(0) scale(1)" : "";
  }, [phase]);

  // Growing past the stacked breakpoint puts the calendar beside the list, where a
  // pinned overlay makes no sense. Drop it immediately rather than animating it away.
  useEffect(() => {
    if (!canExpand && isExpanded) {
      reset();
    }
  }, [canExpand, isExpanded, reset]);

  // The pinned geometry is measured once, so a resize or an orientation change while
  // expanded would leave the panel the wrong size. Re-measure instead of re-animating.
  useEffect(() => {
    if (phase !== "expanded") {
      return;
    }

    const handleResize = () => {
      const area = measureArea();
      if (area) {
        setPanelStyle(area);
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [measureArea, phase]);

  useEffect(() => clearPendingWork, [clearPendingWork]);

  return {
    canExpand,
    isExpanded,
    isAnimating,
    toggleExpanded,
    panelRef,
    areaRef,
    panelStyle,
  };
};
